/**
 * ConditionViewer Component
 *
 * Renders segment conditions in a tree-like visual structure.
 * Similar to CioConditionViewerGroup in ~/code/ui/app/components/cio-condition-viewer-group
 */

import React from 'react';
import { Box, Flex, Text, Stack, Badge, Icon } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { formatFilter, ConditionTypeLabels } from '../../utils/segment-mapping';
import * as styles from './index.css';
import {
  MAX_CONDITION_DEPTH,
  type RelatedLogEntry,
  type ConditionData,
  type GroupCondition,
  type ParsedCondition,
} from './constants';
import { DataSourceLabel } from './data-source-label';
import { parseCondition } from './condition-parsers';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { ConditionItemWrapper } from './condition-item-wrapper';
import {
  enrichConditionWithEventType,
  enrichConditionWithSegmentName,
  enrichConditionWithSegmentsList,
  enrichConditionWithCampaignName,
  enrichConditionWithFormName,
  enrichConditionWithObjectTypeName,
  enrichConditionWithNewsletterName,
  enrichConditionWithMessageName,
  type MessageInfo,
} from './condition-utils';

// Lazy load InlineConditionFetch outside the component to prevent remounts
const LazyInlineConditionFetch = React.lazy(() => import('./inline-condition-fetch'));

const LazySegmentNameWithPreview = React.lazy(() => import('./segment-name-with-preview'));

// =============================================================================
// Types
// =============================================================================

interface ConditionViewerProps {
  /** The condition object to render */
  condition: Record<string, unknown>;
  /** The current nesting depth (0 = top level) */
  depth?: number;
  /** Workspace ID for fetching nested condition references */
  workspaceId?: number;
  /** Whether to hide the "All of the following match:" descriptor */
  hideDescriptor?: boolean;
  /** Maximum depth to render (prevents infinite recursion) */
  maxDepth?: number;
  /** Pre-fetched related logs (condition details) from the backend */
  relatedLogs?: RelatedLogEntry[];
  /** Optional campaigns list for looking up campaign names in message conditions */
  campaigns?: Array<{ id: number; name: string }>;
  /** Optional segments list for looking up segment names in segment conditions */
  segments?: Array<{ id: number; name: string }>;
  /** Optional form ID to name mappings (from segment's linked_form_id -> name) */
  formNames?: Record<string, string>;
  /** Optional object types for relationship conditions */
  objectTypes?: Array<{ id: number; name: string }>;
  /** Optional newsletters list for looking up newsletter names in newsletter_recipients conditions */
  newsletters?: Array<{ id: number; name: string }>;
  /** Optional messages list for looking up message names by output_id (e.g., "output_5") */
  messages?: MessageInfo[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a condition is a group condition (and/or)
 */
function isGroupCondition(condition: ConditionData): condition is GroupCondition {
  return (
    typeof condition === 'object' &&
    condition !== null &&
    ('and' in condition || 'or' in condition || 'not' in condition)
  );
}

/**
 * Check if condition has actual displayable data (not just a reference)
 */
function hasActualConditionData(condition: Record<string, unknown>): boolean {
  // Has nested conditions
  if ('and' in condition || 'or' in condition) return true;
  // Has attribute-style condition fields
  if ('attribute' in condition || 'field' in condition) return true;
  // Has operator (indicates a filter condition)
  if ('operator' in condition && condition.operator != null) return true;
  // Has type that's not just a reference type
  if ('type' in condition && condition.type !== 'segment' && condition.type != null) return true;
  // Has event data
  if ('event' in condition) return true;
  return false;
}

/**
 * Format a single condition for display in human-readable format.
 * Uses shared parsing logic from condition-parsers.ts.
 * Enriches condition data with event type, segment, campaign, and form name info.
 */
function formatSingleCondition(
  condition: Record<string, unknown>,
  relatedLogs?: RelatedLogEntry[],
  campaigns?: Array<{ id: number; name: string }>,
  segments?: Array<{ id: number; name: string }>,
  formNames?: Record<string, string>,
  objectTypes?: Array<{ id: number; name: string }>,
  newsletters?: Array<{ id: number; name: string }>,
  messages?: MessageInfo[],
): ParsedCondition {
  // First, check if we have actual condition data to display
  const hasData = hasActualConditionData(condition);

  // Handle condition_id reference (only if no actual data)
  // Note: Condition details are stored separately and require a separate fetch.
  if (
    !hasData &&
    'condition_id' in condition &&
    typeof condition.condition_id === 'number' &&
    condition.condition_id > 0
  ) {
    const inverse = condition.inverse === true;
    return {
      type: 'condition_reference',
      prefix: inverse ? 'NOT matching' : 'Matches',
      name: `Saved Filter #${condition.condition_id}`,
      dataSourceType: 'attribute',
    };
  }

  // Enrich condition data with event type, segment, campaign, form, object type, newsletter, and message names
  // This is the same enrichment done in InlineConditionFetch for condition_id references
  // Note: enrichConditionWithMessageName should run before enrichConditionWithCampaignName
  // because it adds campaign_id from the message, which enrichConditionWithCampaignName can then use
  let enrichedCondition = condition;
  enrichedCondition = enrichConditionWithEventType(enrichedCondition, relatedLogs);
  enrichedCondition = enrichConditionWithSegmentName(enrichedCondition, relatedLogs);
  // Also enrich with segments list (for segments_negatable conditions with multiple segments)
  enrichedCondition = enrichConditionWithSegmentsList(enrichedCondition, segments);
  enrichedCondition = enrichConditionWithMessageName(enrichedCondition, messages, relatedLogs);
  enrichedCondition = enrichConditionWithCampaignName(enrichedCondition, campaigns);
  enrichedCondition = enrichConditionWithFormName(enrichedCondition, formNames);
  enrichedCondition = enrichConditionWithObjectTypeName(enrichedCondition, objectTypes);
  enrichedCondition = enrichConditionWithNewsletterName(enrichedCondition, newsletters);

  // Use shared parser for all other conditions
  const parsed = parseCondition(enrichedCondition);
  if (parsed != null) {
    return parsed;
  }

  // Fallback - show raw filter representation
  const type = condition.type as string | undefined;
  if (type != null) {
    const typeLabel = ConditionTypeLabels[type] ?? type;
    return {
      type: 'unknown',
      fallback: `${typeLabel}: ${formatFilter(condition)}`,
      dataSourceType: 'unknown',
    };
  }

  return {
    type: 'unknown',
    fallback: formatFilter(condition),
    dataSourceType: 'unknown',
  };
}

// =============================================================================
// Single Condition Component
// =============================================================================

interface SingleConditionProps {
  condition: Record<string, unknown>;
  /** Workspace ID for fetching nested condition references */
  workspaceId?: number;
  /** Pre-fetched related logs (condition details) from the backend */
  relatedLogs?: RelatedLogEntry[];
  /** Optional campaigns list for looking up campaign names */
  campaigns?: Array<{ id: number; name: string }>;
  /** Optional segments list for looking up segment names */
  segments?: Array<{ id: number; name: string }>;
  /** Optional form ID to name mappings */
  formNames?: Record<string, string>;
  /** Optional object types for relationship conditions */
  objectTypes?: Array<{ id: number; name: string }>;
  /** Optional newsletters list for looking up newsletter names */
  newsletters?: Array<{ id: number; name: string }>;
  /** Optional messages list for looking up message names by output_id */
  messages?: MessageInfo[];
}

function SingleCondition({
  condition,
  workspaceId,
  relatedLogs,
  campaigns,
  segments,
  formNames,
  objectTypes,
  newsletters,
  messages,
}: SingleConditionProps) {
  const parts = formatSingleCondition(
    condition,
    relatedLogs,
    campaigns,
    segments,
    formNames,
    objectTypes,
    newsletters,
    messages,
  );

  // If this is a condition_id reference and we have workspaceId, try to fetch details
  const conditionId =
    'condition_id' in condition && typeof condition.condition_id === 'number' ? condition.condition_id : null;

  if (conditionId != null && conditionId > 0 && workspaceId != null && workspaceId > 0) {
    const inverse = condition.inverse === true;

    // Check if we have pre-fetched data for this condition in related logs
    const relatedLog = relatedLogs?.find(
      (log) => log.object_type === 'Condition' && log.object_id === String(conditionId),
    );

    const errorFallback = (
      <ConditionItemWrapper>
        <Text size="s" color="subtle">
          {inverse ? 'NOT ' : ''}Filter #{conditionId} (failed to load)
        </Text>
      </ConditionItemWrapper>
    );

    return (
      <AppErrorBoundary fallback={errorFallback}>
        <React.Suspense
          fallback={
            <ConditionItemWrapper>
              <Text size="s" color="subtle">
                Loading filter #{conditionId}…
              </Text>
            </ConditionItemWrapper>
          }
        >
          <LazyInlineConditionFetch
            conditionId={conditionId}
            workspaceId={workspaceId}
            inverse={inverse}
            relatedLog={relatedLog}
            relatedLogs={relatedLogs}
            campaigns={campaigns}
            formNames={formNames}
            objectTypes={objectTypes}
          />
        </React.Suspense>
      </AppErrorBoundary>
    );
  }

  // Fallback rendering
  if (parts.fallback != null) {
    return (
      <ConditionItemWrapper>
        <Text size="s" color="subtle">
          {parts.fallback}
        </Text>
      </ConditionItemWrapper>
    );
  }

  // Special handling for segment conditions - show popover with segment's conditions on hover
  if (parts.dataSourceType === 'segment' && parts.segmentId != null && workspaceId != null && workspaceId > 0) {
    return (
      <ConditionItemWrapper>
        <React.Suspense
          fallback={
            <>
              <DataSourceLabel type="segment" />
              <Text size="s" color="subtle" style={{ marginTop: themeVars.space['025'] }}>
                {parts.prefix != null ? <>{parts.prefix} </> : null}
                <Text as="span" weight="semibold" color="bold">
                  {parts.name}
                </Text>{' '}
                segment
              </Text>
            </>
          }
        >
          <LazySegmentNameWithPreview
            segmentId={parts.segmentId}
            displayName={parts.name ?? `Segment #${parts.segmentId}`}
            workspaceId={workspaceId}
            inverse={parts.inverse}
            ConditionViewerComponent={ConditionViewer}
          />
        </React.Suspense>
      </ConditionItemWrapper>
    );
  }

  return (
    <ConditionItemWrapper>
      {/* Icon (LabelIcon with purple square) */}
      {parts.dataSourceType != null ? <DataSourceLabel type={parts.dataSourceType} /> : null}

      {/* Content text with condition details */}
      <Text size="s" color="subtle" style={{ marginTop: themeVars.space['025'] }}>
        {/* Prefix (e.g., "have performed", "In") */}
        {parts.prefix != null ? <>{parts.prefix} </> : null}

        {/* Name (attribute name, event name, etc.) - bold */}
        {/* For message conditions, show icon right before message name (after "the [channel]" prefix) */}
        {parts.name != null ? (
          (parts.type === 'message' || parts.type === 'message_action') && parts.messageIcon != null ? (
            (() => {
              // Split name into prefix ("the email" or "the push notification") and actual name ("Email 1")
              // Format is "the ${channelLabel} ${messageName}" where channelLabel can be multi-word
              // If we have channelLabel, use it to split correctly; otherwise fall back to regex
              if (parts.channelLabel != null && parts.name.startsWith(`the ${parts.channelLabel} `)) {
                const prefix = `the ${parts.channelLabel} `;
                const messageName = parts.name.slice(prefix.length);
                return (
                  <>
                    <Text as="span" color="subtle">
                      {prefix}
                    </Text>
                    <Box as="span" display="inline-flex" alignItems="center" gap="025">
                      <Icon name={parts.messageIcon as IconName} size="xs" color="subtle" />
                      <Text as="span" weight="semibold" color="bold">
                        {messageName}
                      </Text>
                    </Box>
                  </>
                );
              }
              // Fallback: try regex pattern for single-word channel labels
              const nameMatch = parts.name.match(/^(the\s+\w+\s+)(.+)$/);
              if (nameMatch != null) {
                const [, prefix, messageName] = nameMatch;
                return (
                  <>
                    <Text as="span" color="subtle">
                      {prefix}
                    </Text>
                    <Box as="span" display="inline-flex" alignItems="center" gap="025">
                      <Icon name={parts.messageIcon as IconName} size="xs" color="subtle" />
                      <Text as="span" weight="semibold" color="bold">
                        {messageName}
                      </Text>
                    </Box>
                  </>
                );
              }
              // Final fallback: if pattern doesn't match, show icon before entire name
              return (
                <Box as="span" display="inline-flex" alignItems="center" gap="025">
                  <Icon name={parts.messageIcon as IconName} size="xs" color="subtle" />
                  <Text as="span" weight="semibold" color="bold">
                    {parts.name}
                  </Text>
                </Box>
              );
            })()
          ) : (
            <Text as="span" weight="semibold" color="bold">
              {parts.name}
            </Text>
          )
        ) : null}

        {/* For message conditions: value comes before operator */}
        {/* Format: "any email in [campaign] has been opened by a human" */}
        {(parts.type === 'message' || parts.type === 'message_action') && parts.value != null ? (
          <>
            {' '}
            {parts.campaignIcon != null && parts.value.startsWith('in ') ? (
              <Box as="span" display="inline-flex" alignItems="center" gap="025">
                <Text as="span" color="subtle">
                  in
                </Text>
                <Icon name={parts.campaignIcon as IconName} size="xs" color="subtle" />
                <Text as="span" weight="semibold" color="bold">
                  {parts.value.replace('in ', '')}
                </Text>
              </Box>
            ) : parts.value.startsWith('in ') ? (
              <>
                <Text as="span" color="subtle">
                  in{' '}
                </Text>
                <Text as="span" weight="semibold" color="bold">
                  {parts.value.replace('in ', '')}
                </Text>
              </>
            ) : (
              <Text as="span" weight="semibold" color="bold">
                {parts.value}
              </Text>
            )}
          </>
        ) : null}

        {/* Operator (e.g., "has been opened", "is equal to", "contains") */}
        {parts.operator != null ? (
          <>
            {' '}
            <Text as="span" weight="semibold" color="bold">
              {parts.operator}
            </Text>
          </>
        ) : null}

        {/* For non-message conditions: value comes after operator */}
        {parts.type !== 'message' && parts.type !== 'message_action' && parts.value != null ? (
          <>
            {' '}
            <Text as="span" weight="semibold" color="bold">
              {parts.value}
            </Text>
          </>
        ) : null}

        {/* Second value for between operators */}
        {parts.value2 != null ? (
          <>
            {' '}
            and{' '}
            <Text as="span" weight="semibold" color="bold">
              {parts.value2}
            </Text>
          </>
        ) : null}

        {/* Suffix (e.g., "at least 3 times within 1 week") */}
        {parts.suffix != null ? <>{parts.suffix}</> : null}
      </Text>
    </ConditionItemWrapper>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ConditionViewer({
  condition,
  depth = 0,
  workspaceId,
  hideDescriptor = false,
  maxDepth = MAX_CONDITION_DEPTH,
  relatedLogs,
  campaigns,
  segments,
  formNames,
  objectTypes,
  newsletters,
  messages,
}: ConditionViewerProps) {
  // Prevent infinite recursion
  if (depth > maxDepth) {
    return (
      <Text size="s" color="subtle">
        (max depth reached)
      </Text>
    );
  }

  // Handle group conditions (and/or/not)
  if (isGroupCondition(condition)) {
    // Handle "not" wrapper: { not: { or: [...] } } or { not: { and: [...] } }
    if ('not' in condition && condition.not != null) {
      const notContent = condition.not as Record<string, unknown>;
      // Unwrap the not and render the inner condition with NOT prefix
      return (
        <Box>
          <Text as="span" weight="semibold" color="bold" size="s" style={{ marginRight: themeVars.space['050'] }}>
            NOT{' '}
          </Text>
          <Box as="span" display="inline">
            <ConditionViewer
              condition={notContent}
              depth={depth + 1}
              workspaceId={workspaceId}
              hideDescriptor={hideDescriptor}
              maxDepth={maxDepth}
              relatedLogs={relatedLogs}
              campaigns={campaigns}
              segments={segments}
              formNames={formNames}
              objectTypes={objectTypes}
              newsletters={newsletters}
              messages={messages}
            />
          </Box>
        </Box>
      );
    }

    // Helper to check if a value has actual children content
    const hasContent = (value: unknown): boolean => {
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === 'object' && value != null) {
        const object = value as Record<string, unknown>;
        return Array.isArray(object.children) && object.children.length > 0;
      }
      return false;
    };

    // Select operator based on which key has actual content, not just existence
    // This handles cases like { and: [], or: [{ field: 'email' }] }
    const operator = hasContent(condition.and)
      ? 'and'
      : hasContent(condition.or)
      ? 'or'
      : 'and' in condition
      ? 'and'
      : 'or';
    const rawChildren = condition[operator];

    // Handle different formats:
    // 1. Array directly: { and: [...] }
    // 2. Object with children: { and: { children: [...] } }
    let children: Record<string, unknown>[];
    if (Array.isArray(rawChildren)) {
      children = rawChildren;
    } else if (rawChildren != null && typeof rawChildren === 'object' && 'children' in rawChildren) {
      const childrenProp = (rawChildren as Record<string, unknown>).children;
      children = Array.isArray(childrenProp) ? childrenProp : [];
    } else {
      children = [];
    }

    // Filter out empty children
    const validChildren = children.filter(
      (child) => child != null && typeof child === 'object' && Object.keys(child).length > 0,
    );

    if (validChildren.length === 0) {
      return (
        <Text size="s" color="subtle">
          No conditions
        </Text>
      );
    }

    // Single child - render directly without group wrapper
    if (validChildren.length === 1) {
      return (
        <ConditionViewer
          condition={validChildren[0]}
          depth={depth}
          workspaceId={workspaceId}
          hideDescriptor={hideDescriptor}
          maxDepth={maxDepth}
          relatedLogs={relatedLogs}
          campaigns={campaigns}
          segments={segments}
          formNames={formNames}
          objectTypes={objectTypes}
          newsletters={newsletters}
          messages={messages}
        />
      );
    }

    return (
      <Stack gap="150">
        {/* Descriptor showing "Matches any/all of X conditions" */}
        {!hideDescriptor ? (
          <Flex gap="050" alignItems="center">
            <Text size="s" color="placeholder">
              Matches
            </Text>
            <Badge>{operator === 'and' ? 'all' : 'any'}</Badge>
            <Text size="s" color="placeholder">
              of {validChildren.length} conditions
            </Text>
          </Flex>
        ) : null}

        {/* Condition list */}
        <Box
          className={depth > 0 ? styles.nestedGroup : undefined}
          pl={depth > 0 ? '200' : '0'}
          style={depth > 0 ? { borderLeft: `2px solid ${themeVars.color['border-minimal']}` } : undefined}
        >
          <Stack gap="150">
            {validChildren.map((child, index) => (
              <Box key={index} w="full">
                <ConditionViewer
                  condition={child}
                  depth={depth + 1}
                  workspaceId={workspaceId}
                  hideDescriptor={false}
                  maxDepth={maxDepth}
                  relatedLogs={relatedLogs}
                  campaigns={campaigns}
                  segments={segments}
                  formNames={formNames}
                  objectTypes={objectTypes}
                  newsletters={newsletters}
                  messages={messages}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  }

  // Handle single condition
  return (
    <SingleCondition
      condition={condition}
      workspaceId={workspaceId}
      relatedLogs={relatedLogs}
      campaigns={campaigns}
      segments={segments}
      formNames={formNames}
      objectTypes={objectTypes}
      newsletters={newsletters}
      messages={messages}
    />
  );
}
